import { Grid, styled } from "@mui/material";
import { IStyledChatMessageProps } from "./types";

const StyledMessage = styled(Grid, {
    shouldForwardProp: (prop) =>
        prop !== "answer" && prop !== "context" && prop !== "plainAnswer",
})<IStyledChatMessageProps>(({ theme, answer, plainAnswer }) => {
    const footerStyles = {
        "& .chat-message-footer": {
            marginTop: theme.spacing(0.25),
            // Hidden until the message is hovered — pointerEvents off too, so a hidden copy/feedback
            // button can't be clicked (via keyboard focus, say) while it's invisible.
            opacity: 0,
            pointerEvents: "none",
            transition: "opacity 0.15s ease",
            "&>.MuiGrid-root": {
                // The top margin MessageActions ships with by default (for when it stands alone, without
                // a timestamp next to it) doesn't apply here — the spacing already comes from the footer itself.
                marginTop: 0,
            },
        },
        "&:hover .chat-message-footer": {
            opacity: 1,
            pointerEvents: "auto",
        },
        "& .MuiIconButton-root": {
            width: theme.spacing(3),
            height: theme.spacing(3),
            "& .MuiSvgIcon-root": {
                fontSize: theme.spacing(2),
            },
        },
    };

    // The message bubble/attachment card/context header intentionally break from the rest of the system's
    // squared-off 4px radius — a chat bubble calls for more curve, just not the full 16px pill look.
    // A literal value (the general scale has no step between 8px and 16px) — kept in sync with the
    // same value in AttachmentCard/styled.ts so the bubble and the file cards are equally rounded.
    const radius = "12px";

    // Shared by both the sent and received message — MessageBubble renders the same classes either way,
    // only toggling the "--plain" modifier and the "--start"/"--end" alignment.
    const bubbleStyles = {
        // Groups attachments and the context+bubble unit as two independent blocks — each sized to its
        // own content, both pinned to the same edge (so a short "teste" bubble and a long filename don't
        // have to share a width, but a context header and the bubble it's glued to always do).
        "& .chat-bubble-stack": {
            display: "flex",
            flexDirection: "column",
            maxWidth: "100%",
        },
        "& .chat-bubble-stack--end": {
            width: "fit-content",
            marginLeft: "auto",
            alignItems: "flex-end",
        },
        "& .chat-bubble-stack--start": {
            width: "100%",
            // No alignItems override here — the received bubble still needs to stretch to the full width,
            // it doesn't hug its own content like the sent side does.
        },
        // Context header + bubble, as one unit: only these two stretch to share a width with each other.
        "& .chat-bubble-group": {
            display: "flex",
            flexDirection: "column",
            maxWidth: "100%",
        },
        // fit-content only on the sent side — keeps this pair from being stretched by a wider attachment
        // box next to it. On the received side it should still stretch to the full message width.
        "& .chat-bubble-stack--end .chat-bubble-group": {
            width: "fit-content",
        },
        "& .chat-bubble": {
            padding: theme.spacing(1.5, 1.75),
            borderRadius: radius,
            backgroundColor: (theme as any).palette.neutral.darker,
            color: (theme as any).palette.neutral.darker,
            maxWidth: "100%",
            minWidth: 0,
            overflowX: "auto",
            overflowWrap: "break-word",
            wordBreak: "break-word",
            "& table": {
                display: "block",
                width: "max-content",
                maxWidth: "100%",
                borderCollapse: "collapse",
                color: "inherit",
                margin: `${theme.spacing(1)} 0`,
                whiteSpace: "nowrap",
                overflowX: "auto",
            },
            "& th, & td": {
                border: "1px solid #ffffff",
                padding: theme.spacing(0.5),
            },
        },
        // Same white box as the composer's "Context: X" banner — glued flush on top of the bubble, no gap,
        // exactly like that banner sits flush on top of the composer's input.
        "& .chat-context-header": {
            // No explicit width here — it needs to stay auto so .chat-bubble-group's default
            // align-items:stretch can actually make it match the bubble below it.
            backgroundColor: (theme as any).palette.common.white,
            borderRadius: `${radius} ${radius} 0 0`,
            padding: theme.spacing(0.75, 1.25),
        },
        "& .chat-context-header + .chat-bubble": {
            borderRadius: `0 0 ${radius} ${radius}`,
        },
        // A row of the shared square AttachmentCard components above the bubble — just the wrapping
        // layout lives here, wrapping onto more rows if there isn't room for them side by side.
        "& .chat-attachments": {
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "flex-end",
            width: "fit-content",
            maxWidth: "100%",
            gap: theme.spacing(1),
            marginBottom: theme.spacing(1.5),
        },
        // Same box (padding, radius, width) as the sent message — just no fill color, for the received one.
        "& .chat-bubble--plain": {
            backgroundColor: "transparent",
            color: (theme as any).palette.neutral?.lighter ?? "#fff",
        },
    };

    if (plainAnswer) {
        return {
            justifyContent: "flex-start",
            width: "100%",
            minWidth: 0,
            ...footerStyles,
            ...bubbleStyles,
            "& .chat-streaming-status": {
                color: (theme as any).palette.neutral?.lighter ?? "#fff",
            },
            "& .chat-assistant-body": {
                // Set explicitly here (not just inherited from .chat-bubble--plain) so the text stays
                // legible even if this message is ever shown with withBackground on.
                color: (theme as any).palette.neutral?.lighter ?? "#fff",
                // The markdown here renders as plain tags (div, li, strong...), not <Typography>, so unlike
                // the sent message it never inherits the app's font from anywhere — it has to be set here.
                fontFamily: theme.typography.fontFamily,
                fontSize: "0.875rem",
                lineHeight: 1.55,
                overflowWrap: "break-word",
                wordBreak: "break-word",
                minWidth: 0,
            },
        };
    }

    return {
        justifyContent: `${answer ? "flex-start" : "flex-end"}`,
        minWidth: 0,
        "&>.MuiGrid-root": {
            minWidth: 0,
            maxWidth: "100%",
        },
        ...bubbleStyles,
        ...footerStyles,
    };
});

export { StyledMessage };
